import React, { useCallback, useEffect, useState } from 'react';
import T from './theme';

// API tokens for the athlete's own tools (e.g. a coaching assistant that reads
// the day and logs meals on request). A token is shown once, at creation;
// UMAX keeps only a hash. "read" = analysis, meals, estimate; "meals:write" =
// log / edit / delete meals; "recipes:write" = save / edit / delete recipes. Tokens never reach settings, sync, or accounts.
// Permissions on a live token can be changed here (session only); the secret and expiry do not change.

export const SCOPE_LABELS = {
  read: 'Read my day, meals, and body metrics',
  'meals:write': 'Log, edit, and delete my meals',
  'recipes:write': 'Save, edit, and delete my recipes',
};

export default function TokensPanel({ api }) {
  const [tokens, setTokens] = useState(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState({ read: true, 'meals:write': false, 'recipes:write': false });
  const [days, setDays] = useState(90);
  const [fresh, setFresh] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [editing, setEditing] = useState(null); // token id whose permissions are open
  const [savedId, setSavedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/me/tokens');
      setTokens(res.data?.tokens || []);
    } catch (e) {
      setError('Could not load tokens');
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const chosen = Object.keys(scopes).filter((k) => scopes[k]);
      const res = await api.post('/me/tokens', { name: name.trim(), scopes: chosen, expires_in_days: days });
      setFresh(res.data);
      setName('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not create the token');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/me/tokens/${id}`);
      setConfirmRevoke(null);
      if (fresh?.id === id) setFresh(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not revoke the token');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (t) => {
    setConfirmRevoke(null);
    setSavedId(null);
    setEditing(t.id);
  };

  // A tick applies at once: no second Save to miss (the Settings Save below
  // this panel does not touch tokens). Same secret, same expiry.
  const toggleScope = async (t, key, on) => {
    const chosen = Object.keys(SCOPE_LABELS).filter((k) => (k === key ? on : t.scopes.includes(k)));
    if (!chosen.length) return;
    setBusy(true);
    setError(null);
    setSavedId(null);
    try {
      await api.patch(`/me/tokens/${t.id}`, { scopes: chosen });
      await load();
      setSavedId(t.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not change the permissions');
    } finally {
      setBusy(false);
    }
  };

  const noScope = !Object.values(scopes).some(Boolean);
  const active = (tokens || []).filter((t) => !t.revoked && !t.expired);

  return (
    <div style={{ marginTop: 16 }} data-testid="tokens-panel">
      <h3 style={{ margin: '0 0 4px' }}>API tokens</h3>
      <p style={{ color: T.color.textLight, fontSize: T.font.sm, margin: '0 0 8px' }}>
        For your own tools. A token can only do what you tick, only for your account, and never
        changes settings or syncs. It is shown once; revoke it any time.
      </p>
      {error && <p role="alert" style={{ color: T.color.danger, fontSize: T.font.sm }}>{error}</p>}

      {fresh && (
        <div style={{ border: `1px solid ${T.color.border}`, borderRadius: 8, padding: 8, marginBottom: 8, fontSize: T.font.sm }}>
          <strong>Copy it now: “{fresh.name}” will not be shown again.</strong>
          <input
            readOnly
            value={fresh.token}
            aria-label="new token"
            onFocus={(e) => e.target.select()}
            style={{ display: 'block', width: '100%', fontFamily: 'monospace', marginTop: 4 }}
          />
          <button type="button" onClick={() => setFresh(null)} style={{ marginTop: 6 }}>Done</button>
        </div>
      )}

      <h4 style={{ margin: '8px 0 4px' }}>New token</h4>
      <label style={{ display: 'block', fontSize: T.font.sm }}>
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="amelia"
          aria-label="token name"
          maxLength={40}
          style={{ display: 'block', width: 200 }}
        />
      </label>
      {Object.entries(SCOPE_LABELS).map(([key, label]) => (
        <label key={key} style={{ display: 'block', fontSize: T.font.sm }}>
          <input
            type="checkbox"
            checked={Boolean(scopes[key])}
            onChange={(e) => setScopes({ ...scopes, [key]: e.target.checked })}
            aria-label={`scope ${key}`}
          />{' '}
          {label}
        </label>
      ))}
      <label style={{ display: 'block', fontSize: T.font.sm, marginTop: 4 }}>
        Expires after{' '}
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="token expiry">
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
          <option value={365}>1 year</option>
        </select>
      </label>
      <button type="button" onClick={create} disabled={busy || !name.trim() || noScope} style={{ marginTop: 6 }}>
        Create token
      </button>

      {tokens && active.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', fontSize: T.font.sm }}>
          {active.map((t) => (
            <li key={t.id} data-testid={`token-${t.id}`} style={{ padding: '4px 0', borderBottom: `1px solid ${T.color.border}` }}>
              <strong>{t.name}</strong> <code>{t.prefix}…</code> · {t.scopes.join(', ')}
              <span style={{ color: T.color.textLight }}>
                {' '}· {t.last_used_at ? `used ${new Date(t.last_used_at).toLocaleString()}` : 'never used'}
                {t.expires_at ? ` · expires ${new Date(t.expires_at).toLocaleDateString()}` : ''}
              </span>{' '}
              {editing === t.id ? (
                <div style={{ margin: '4px 0 2px 12px' }}>
                  {Object.entries(SCOPE_LABELS).map(([key, label]) => {
                    const on = t.scopes.includes(key);
                    return (
                      <label key={key} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={busy || (on && t.scopes.length === 1)}
                          onChange={(e) => toggleScope(t, key, e.target.checked)}
                          aria-label={`${t.name} scope ${key}`}
                        />{' '}
                        {label}
                      </label>
                    );
                  })}
                  <span role="status" style={{ color: T.color.textLight }}>
                    {savedId === t.id ? 'Saved. ' : 'Changes apply when you tick. '}
                  </span>
                  <button type="button" onClick={() => setEditing(null)} aria-label={`done editing ${t.name}`}>Done</button>
                </div>
              ) : confirmRevoke === t.id ? (
                <>
                  <button type="button" onClick={() => revoke(t.id)} disabled={busy} aria-label={`really revoke ${t.name}`}>
                    Revoke it
                  </button>{' '}
                  <button type="button" onClick={() => setConfirmRevoke(null)}>Keep</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => startEdit(t)} aria-label={`edit permissions for ${t.name}`}>
                    Permissions…
                  </button>{' '}
                  <button type="button" onClick={() => { setEditing(null); setConfirmRevoke(t.id); }}>Revoke…</button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
